import { usuarioEhEstudante, usuarioEhCiee, NIVEL_CONSULTOR, NIVEL_ESTUDANTE } from "../../utils/permissoes.js";
import { CieeAcompanhamentoStrategy } from "./CieeAcompanhamentoStrategy.js";
import { EstudanteAcompanhamentoStrategy } from "./EstudanteAcompanhamentoStrategy.js";
import { EmpresaAcompanhamentoStrategy } from "./EmpresaAcompanhamentoStrategy.js";
import { ConsultorAcompanhamentoStrategy } from "./ConsultorAcompanhamentoStrategy.js";
import { criarLogger } from "../acompanhamentoHelpers.js";

const logFactory = criarLogger("Strategy:Factory");

/**
 * Fábrica de estratégias de acompanhamento.
 *
 * Princípios SOLID:
 * - OCP (Open/Closed): Para suportar novos perfis no sistema (ex.: Instituição de Ensino),
 *   basta adicionar a nova estratégia aqui sem alterar o código do orquestrador ou da UI.
 * - SRP (Single Responsibility): Responsabilidade única de inspecionar a sessão e
 *   instanciar a estratégia correta.
 */
export class AcompanhamentoStrategyFactory {
  /**
   * Obtém a estratégia adequada para a identidade e payload do usuário logado.
   *
   * @param {object} identidade - Objeto de identidade retornado por obterIdentidadeSessao()
   * @param {object} [tokenPayload={}] - Claims do JWT decodificado
   * @param {number} [nivel] - Nível de acesso numérico da sessão
   * @returns {import("./IAcompanhamentoStrategy.js").IAcompanhamentoStrategy}
   */
  static criar({ identidade = {}, tokenPayload = {}, nivel } = {}) {
    const ehCiee = usuarioEhCiee(tokenPayload, identidade);
    const ehEstudante =
      nivel === NIVEL_ESTUDANTE ||
      usuarioEhEstudante(tokenPayload) ||
      identidade?.tipo === "estudante" ||
      identidade?.tipo === "aprendiz";

    // 1. CIEE / SEOPE / SECEX / INFOR / Administrador têm acesso global irrestrito
    if (ehCiee) {
      logFactory("Estratégia selecionada: CieeAcompanhamentoStrategy (visão total multi-programa)");
      return new CieeAcompanhamentoStrategy();
    }

    // 2. Estudante / Aprendiz vê apenas as autorizações vinculadas ao seu CPF
    if (ehEstudante) {
      logFactory("Estratégia selecionada: EstudanteAcompanhamentoStrategy (vínculo por CPF)");
      return new EstudanteAcompanhamentoStrategy();
    }

    // 3. Consultor externo legado vê apenas as empresas vinculadas ao seu cadastro
    if (nivel === NIVEL_CONSULTOR) {
      logFactory("Estratégia selecionada: ConsultorAcompanhamentoStrategy");
      return new ConsultorAcompanhamentoStrategy();
    }

    // 4. Perfil padrão: Empresa (isolamento por estabelecimento / CNPJ)
    logFactory("Estratégia selecionada: EmpresaAcompanhamentoStrategy");
    return new EmpresaAcompanhamentoStrategy();
  }
}
